import useIsMobile from 'hooks/useIsMobile';
import React, { useEffect, useMemo } from 'react';
import Head from 'next/head';
import { v4 as uuid } from 'uuid';
import { Box, Typography } from '@mui/material';
import IllustrationsService from 'services/IllustrationsService';
import GalleryLayout from 'layouts/GalleryLayout';
import FriendsReferralBanner from 'components/Invite/FriendsReferralBanner';
import { useRouter } from 'next/router';
import createSlug from 'utils/createSlug';
import { useBreadcrumbsContext } from 'contexts/breadcrumbsContext';
import { useGalleryContext, GalleryType } from 'contexts/galleryContext';
import IllustrationThumb from 'components/IllustrationThumb';
import IllustrationsCategorySection from 'components/IllustrationCategorySection';
import { setLocale } from 'utils/locale';
import FlagsSelect from 'components/FlagsSelect';
import { useQuery } from '@tanstack/react-query';
import Breadcrumb from 'components/Templates/Breadcrumb';

import en from '../../locales/illustrations/en';
import ja from '../../locales/illustrations/en';
import zh from '../../locales/illustrations/en';
import es from '../../locales/illustrations/es';
import fr from '../../locales/illustrations/fr';
import it from '../../locales/illustrations/it';

export async function getStaticProps({ locale }) {
  if (locale === 'ja' || locale === 'zh') {
    locale = 'en';
  }
  return {
    props: {
      subcategories: await IllustrationsService.findByStudyField({}, locale),
    },
    revalidate: 604800, // 1 week
  };
}

export default function Illustrations({ subcategories }) {
  const router = useRouter();
  const [, dispatchBreadcrumbs] = useBreadcrumbsContext();
  const [, dispatchGallery] = useGalleryContext();
  const isMobile = useIsMobile();
  const { search } = router.query;
  let { locale } = router;
  if (locale === 'ja' || locale === 'zh') {
    locale = 'en';
  }

  const t = setLocale(locale, { en, es, fr, it, ja, zh });

  const { data } = useQuery({
    queryKey: ['subcategories'],
    queryFn: async () => {
      return await IllustrationsService.findByStudyField({}, locale);
    },
    initialData: subcategories,
    retryOnMount: false,
  });

  useEffect(() => {
    dispatchBreadcrumbs([], true);
    dispatchGallery({ type: GalleryType.Illustration, activeTag: undefined });
  }, []);

  const galleryContent = useMemo(
    () => (
      <GalleryLayout category={'base_gallery'} label={''}>
        <Box pt={3} mb={6}>
          <Breadcrumb items={[{ label: 'Illustrations', path: router.asPath }]} />
        </Box>

        <Typography variant={!isMobile ? 'h5' : 'body2'} component="h1">
          Explore our 50+ scientific icon and illustration categories
        </Typography>

        {/* Description under H1 */}
        <Typography variant="body1" mt={2}>
          Access high-quality scientific icons, illustrations, images, and drawings from 50+
          categories. Designed for researchers, students, and scientists.
        </Typography>

        {data.slice(0, isMobile ? 4 : data.length).map(({ name, illustrations }) => (
          <IllustrationsCategorySection
            title={<h2>{t[name] || name}</h2>}
            onTitleClick={() => router.push(`/illustrations/subcategory/${createSlug(name)}`)}
            key={uuid()}
          >
            {illustrations.map((illustration, index) => {
              if (illustration.tags.some((tag) => ['penis', 'vagina'].includes(tag))) {
                return null;
              }

              if (index >= (isMobile ? 4 : 12)) {
                return null;
              }

              return <IllustrationThumb key={uuid()} {...illustration} />;
            })}
          </IllustrationsCategorySection>
        ))}

        <Box pt={5} mt={10} mb={2}>
          <FriendsReferralBanner />
        </Box>

        <FlagsSelect />
      </GalleryLayout>
    ),
    [router.path, isMobile, locale],
  );

  return (
    <>
      <Head>
        <title>
          {search
            ? `Scientific Illustrations of ${search} - Mind the Graph`
            : `Scientific Illustrations, Icons, Images & Drawings - Mind the Graph`}
        </title>

        <meta
          key="og:description"
          name="description"
          content="Find scientific illustrations, scientific icons, scientific images, scientific drawings, and scientific pictures at Mind the Graph. Create impactful graphical abstracts, posters, and presentations for research our intuitive platform. Start creating now."
        />
      </Head>
      {galleryContent}
    </>
  );
}
